import type {
  CaseQuestionItem,
  CaseQuestionOption,
  QuestionAnswerPayloadItem,
} from "services/cases.service";
import type { NormalizedOption } from "./types";
import { QUESTION_TYPE } from "./constants";

export function booleanAnsToYesNoString(v: unknown): string | undefined {
  if (v === true || v === 1) return "true";
  if (v === false || v === 0) return "false";
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (["true", "yes", "y", "1"].includes(s)) return "true";
    if (["false", "no", "n", "0"].includes(s)) return "false";
  }
  return undefined;
}

export function isCaseQuestionBooleanType(q: CaseQuestionItem): boolean {
  const t = (q.question_type ?? QUESTION_TYPE.TEXT_ANSWER).toUpperCase();
  return t === QUESTION_TYPE.BOOLEAN;
}

function isTruthyFlag(v: unknown): boolean {
  return v === true || v === 1 || v === "1" || String(v).toLowerCase() === "true";
}

function isExplicitFalseFlag(v: unknown): boolean {
  if (v === false || v === 0) return true;
  const normalized = String(v ?? "")
    .trim()
    .toLowerCase();
  return normalized === "false" || normalized === "0";
}

function coercePayloadNumericId(v: unknown): string | number {
  if (v === undefined || v === null || v === "") return "";
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const s = String(v).trim();
  if (s !== "" && /^-?\d+$/.test(s)) return Number(s);
  return s;
}

const OPTION_DISPLAY_TEXT_KEYS = [
  "option_text",
  "option_label",
  "option",
  "option_name",
  "label",
  "name",
  "title",
  "text",
  "description",
] as const;

function firstNonEmptyStringFromKeys(
  obj: Record<string, unknown>,
  keys: readonly string[]
): string {
  for (const k of keys) {
    const v = obj[k];
    if (v === undefined || v === null) continue;
    const s = String(v).trim();
    if (s !== "") return s;
  }
  return "";
}

export function isCaseQuestionActiveForUi(q: CaseQuestionItem): boolean {
  const r = q as Record<string, unknown>;
  const v = r.is_active ?? q.is_active;
  return isTruthyFlag(v);
}

export function filterCaseQuestionsForUi(questions: CaseQuestionItem[]): CaseQuestionItem[] {
  return questions.filter(isCaseQuestionActiveForUi);
}

export function isCaseQuestionRequired(q: CaseQuestionItem): boolean {
  if (!isCaseQuestionActiveForUi(q)) return false;
  const r = q as Record<string, unknown>;
  if (
    isTruthyFlag(r.is_mandatory) ||
    isTruthyFlag(r.mandatory) ||
    isTruthyFlag(r.required) ||
    isTruthyFlag(r.is_required)
  ) {
    return true;
  }
  if (
    isExplicitFalseFlag(r.is_mandatory) ||
    isExplicitFalseFlag(r.mandatory) ||
    isExplicitFalseFlag(r.required) ||
    isExplicitFalseFlag(r.is_required)
  ) {
    return false;
  }
  if (q.is_optional === true || r.is_optional === true) return false;
  if (q.is_optional === false || r.is_optional === false) return true;
  if (q.is_optional !== undefined && q.is_optional !== null) {
    return !isTruthyFlag(q.is_optional);
  }
  if (r.is_optional !== undefined && r.is_optional !== null) {
    return !isTruthyFlag(r.is_optional);
  }
  return true;
}

export function isCaseQuestionMandatoryForActionBanner(q: CaseQuestionItem): boolean {
  if (!isCaseQuestionActiveForUi(q)) return false;
  const r = q as Record<string, unknown>;
  if (
    isTruthyFlag(r.is_mandatory) ||
    isTruthyFlag(r.mandatory) ||
    isTruthyFlag(r.required) ||
    isTruthyFlag(r.is_required)
  ) {
    return true;
  }
  if (
    isExplicitFalseFlag(r.is_mandatory) ||
    isExplicitFalseFlag(r.mandatory) ||
    isExplicitFalseFlag(r.required) ||
    isExplicitFalseFlag(r.is_required)
  ) {
    return false;
  }
  if (q.is_optional === true || r.is_optional === true) return false;
  if (q.is_optional === false || r.is_optional === false) return true;
  if (q.is_optional !== undefined && q.is_optional !== null) {
    return !isTruthyFlag(q.is_optional);
  }
  if (r.is_optional !== undefined && r.is_optional !== null) {
    return !isTruthyFlag(r.is_optional);
  }
  return false;
}

export function normalizePersistedMultiAnswerSelections(q: CaseQuestionItem): string[] | undefined {
  const multi = q.multi_answer;
  if (!Array.isArray(multi) || multi.length === 0) return undefined;
  const opts = Array.isArray(q.options) ? (q.options as CaseQuestionOption[]) : [];
  const out = multi
    .map((item) => {
      if (typeof item === "string") return item.trim();
      if (!item || typeof item !== "object") return "";
      const o = item as Record<string, unknown>;
      const idStr = String(o.option_id ?? "").trim();
      const fromOpt = idStr
        ? opts.find((opt) => String(opt.option_id ?? "").trim() === idStr)
        : undefined;
      if (fromOpt) {
        const oid = String(fromOpt.option_id ?? "").trim();
        const val = String(fromOpt.option_value ?? "").trim();
        const text = firstNonEmptyStringFromKeys(
          fromOpt as Record<string, unknown>,
          OPTION_DISPLAY_TEXT_KEYS
        );
        return (
          (val !== "" && val !== oid ? val : "") ||
          (text !== "" ? text : "") ||
          val ||
          String(o.option ?? o.option_value ?? o.option_id ?? "")
        ).trim();
      }
      return String(o.option ?? o.option_value ?? o.option_id ?? "").trim();
    })
    .filter((s) => s !== "");
  return out.length ? out : undefined;
}

export function normalizeOptions(options: CaseQuestionItem["options"]): NormalizedOption[] {
  if (!Array.isArray(options) || options.length === 0) return [];
  return options.map((opt) => {
    if (typeof opt === "string") return { value: opt, label: opt };
    const o = opt as CaseQuestionOption;
    const r = o as Record<string, unknown>;
    const idStr =
      o.option_id !== undefined && o.option_id !== null ? String(o.option_id).trim() : "";
    const rawVal = String(o.option_value ?? "").trim();
    const rawText = firstNonEmptyStringFromKeys(r, OPTION_DISPLAY_TEXT_KEYS);
    const value =
      (rawVal !== "" && rawVal !== idStr ? rawVal : "") ||
      (rawText !== "" ? rawText : "") ||
      rawVal ||
      idStr;
    const label = rawText || (rawVal !== "" && rawVal !== idStr ? rawVal : "") || value;
    return { value, label };
  });
}

export function selectedValuesFromQuestionOptions(q: CaseQuestionItem): string[] {
  const opts = q.options;
  if (!Array.isArray(opts) || opts.length === 0) return [];
  const norm = normalizeOptions(opts);
  const out: string[] = [];
  opts.forEach((opt, i) => {
    if (typeof opt === "string") return;
    const o = opt as Record<string, unknown>;
    const sel =
      o.selected === true ||
      o.selected === 1 ||
      String(o.selected ?? "")
        .trim()
        .toLowerCase() === "true";
    if (sel && norm[i]) out.push(norm[i].value);
  });
  return out;
}

export function buildQuestionAnswersPayload(
  questions: CaseQuestionItem[],
  answers: Record<number, string | string[]>
): QuestionAnswerPayloadItem[] {
  const isSingleChoiceType = (type: string) =>
    type === QUESTION_TYPE.RADIO ||
    type === QUESTION_TYPE.SINGLE ||
    type === QUESTION_TYPE.SINGLE_CHOICE ||
    type === QUESTION_TYPE.SINGLE_ANSWER;
  const isMultiChoiceType = (type: string) =>
    type === QUESTION_TYPE.CHECKBOX ||
    type === QUESTION_TYPE.MULTIPLE ||
    type === QUESTION_TYPE.MULTIPLE_CHOICE ||
    type === QUESTION_TYPE.MULTIPLE_ANSWER;
  const isBooleanType = (type: string) => type === QUESTION_TYPE.BOOLEAN;
  const toBooleanValue = (value: string | string[] | undefined) => {
    if (Array.isArray(value)) return "";
    const normalized = String(value ?? "")
      .trim()
      .toLowerCase();
    if (["true", "yes", "y", "1"].includes(normalized)) return true;
    if (["false", "no", "n", "0"].includes(normalized)) return false;
    return "";
  };
  const normalizeOptionMatchValue = (value: string) => value.trim().toLowerCase();
  const resolveOption = (q: CaseQuestionItem, rawValue: string) => {
    const normalized = normalizeOptionMatchValue(rawValue);
    const options = Array.isArray(q.options) ? (q.options as CaseQuestionOption[]) : [];
    return options.find((opt) => {
      const r = opt as Record<string, unknown>;
      const id = String(opt.option_id ?? "")
        .trim()
        .toLowerCase();
      const optionValue = String(opt.option_value ?? "")
        .trim()
        .toLowerCase();
      const optionField = String(r.option ?? "")
        .trim()
        .toLowerCase();
      const optionText = firstNonEmptyStringFromKeys(
        opt as Record<string, unknown>,
        OPTION_DISPLAY_TEXT_KEYS
      )
        .trim()
        .toLowerCase();
      return (
        normalized !== "" &&
        (normalized === id ||
          normalized === optionValue ||
          normalized === optionText ||
          normalized === optionField)
      );
    });
  };

  const multiAnswerOptionPayloadValue = (
    opt: CaseQuestionOption | undefined,
    selected: string
  ): string => {
    if (!opt) return String(selected);
    const idStr = String(opt.option_id ?? "").trim();
    const val = String(opt.option_value ?? "").trim();
    const text = firstNonEmptyStringFromKeys(
      opt as Record<string, unknown>,
      OPTION_DISPLAY_TEXT_KEYS
    );
    if (val !== "" && val !== idStr) return val;
    if (text !== "") return text;
    const sel = String(selected).trim();
    if (sel !== "" && sel !== idStr) return sel;
    return val || text || sel;
  };

  return questions.filter(isCaseQuestionActiveForUi).map((q) => {
    const value = answers[q.question_id];
    const type = (q.question_type ?? QUESTION_TYPE.TEXT_ANSWER).toUpperCase();
    const base: QuestionAnswerPayloadItem = {
      question_id: q.question_id,
      boolean_ans: "",
      text_answer: "",
      single_answer: "",
      multi_answer: [],
    };
    if (value === undefined || value === null) return base;
    if (isBooleanType(type)) {
      base.boolean_ans = toBooleanValue(value);
      return base;
    }
    if (isSingleChoiceType(type)) {
      const selected =
        typeof value === "string" ? value : Array.isArray(value) ? (value[0] ?? "") : "";
      const matched = selected ? resolveOption(q, selected) : undefined;
      base.single_answer = coercePayloadNumericId(
        matched?.option_id ?? matched?.option_value ?? selected
      ) as string | number;
      return base;
    }
    if (isMultiChoiceType(type)) {
      const selectedList = Array.isArray(value) ? value : value ? [value] : [];
      base.multi_answer = selectedList.map((selected) => {
        const matched = resolveOption(q, selected);
        return {
          option: multiAnswerOptionPayloadValue(matched, selected),
          option_id: coercePayloadNumericId(matched?.option_id ?? selected) as string | number,
        };
      });
      return base;
    }
    base.text_answer =
      typeof value === "string" ? value : Array.isArray(value) ? value.join(", ") : "";
    return base;
  });
}

export function isAllRequiredAnswered(
  questions: CaseQuestionItem[],
  answers: Record<number, string | string[]>
): boolean {
  const activeOnly = questions.filter(isCaseQuestionActiveForUi);
  const required = activeOnly.filter((q) => isCaseQuestionRequired(q));
  if (required.length === 0) return true;
  return required.every((q) => {
    const v = answers[q.question_id];
    if (v === undefined) return false;
    if (typeof v === "string") return v.trim() !== "";
    return Array.isArray(v) && v.length > 0;
  });
}
